import { useEffect, useMemo, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';
import { formatTimeAgo } from './DateText';
import { ItemCard, ItemIdentity, StatusBadge } from './ui/ItemCard';
import { formatLabel, getItemImageUrl, imageUrl } from '../utils/itemUtil';
import { seedItemImage, useItemImageUrl } from '../utils/itemImageCache';

// Maps a feed entry to what the card shows. Only payloads that carry an
// item's public id produce an item (and a link); Comment, Like and
// ItemUpdate events expose no usable item reference, so none is invented.
export const describeActivity = activity => {
    const event = activity.event || {};
    switch (activity.type) {
        case "UserItemAction":
            return {
                verb: formatLabel(event.action) || 'Shared an experience',
                badge: formatLabel(event.action),
                item: event.itemId ? { id: event.itemId, name: event.itemName, tag: { name: event.itemTagName } } : null,
                photo: imageUrl(event.imageFilename),
                // The event omits the item's image; it is looked up by id.
                lookupItemImage: Boolean(event.itemId),
            };
        case "UserItem":
            return { verb: 'Did something', item: event.item?.id ? event.item : null, fullItem: event.item };
        case "Like":
            return { verb: 'Liked an item', item: null };
        case "Comment":
            return { verb: 'Commented', item: null, story: event.text };
        case "Item":
            return { verb: 'Added an item', badge: 'Added', item: event.id ? event : null, fullItem: event.id ? event : null };
        case 'ItemUpdate':
            return { verb: 'Updated an item', item: null };
        default:
            return { verb: formatLabel(activity.type), item: null };
    }
}

export const Activity = ({ activity }) => {

    const user = useSelector(state => state.login.user);
    const eventUser = activity.user;
    const { verb, badge, item, photo, story, fullItem, lookupItemImage } = describeActivity(activity);
    const isLoggedInUser = eventUser?.id && eventUser?.id === user?.id;
    const createdAt = activity.createdAt ? parseISO(activity.createdAt) : null;
    const [photoFailed, setPhotoFailed] = useState(false);

    useEffect(() => {
        setPhotoFailed(false);
    }, [photo]);

    useEffect(() => {
        if (fullItem) seedItemImage(fullItem);
    }, [fullItem]);

    // Experience photo first; the item's default image is needed only when
    // there is no photo or it failed, and the placeholder comes last.
    const needsItemImage = lookupItemImage && (!photo || photoFailed);
    const lookedUpImage = useItemImageUrl(item?.id, needsItemImage);
    const itemImage = fullItem ? getItemImageUrl(fullItem) : lookedUpImage;

    const sources = useMemo(() => [
        photo ? { url: photo, alt: item?.name ? `Photo shared for ${item.name}` : 'Shared photo' } : null,
        itemImage ? { url: itemImage, alt: item?.name ? `Image for ${item.name}` : '' } : null,
    ], [photo, itemImage, item?.name]);

    return (
        <ItemCard
            imageSources={sources}
            mediaResetKey={activity.id || item?.id}
            onImageFailed={url => { if (url === photo) setPhotoFailed(true); }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <UserAvatar user={eventUser} size="small" />
                <Box sx={{ minWidth: 0 }}>
                    {isLoggedInUser ?
                        <Typography component="span" sx={{ fontWeight: 600 }}>You</Typography>
                        : eventUser ?
                            <Link component={RouterLink} to={`/user/${eventUser.id}/items`} color="text.primary" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                                {eventUser.name}
                            </Link>
                            : null
                    }
                    <Typography variant="caption" component="div" color="text.secondary">
                        {verb}{createdAt ? ` · ${formatTimeAgo(createdAt)}` : ''}
                    </Typography>
                </Box>
            </Box>
            {item ?
                <ItemIdentity item={item} sx={{ mt: '20px', mb: story ? '15px' : 0 }}>
                    {badge ? <StatusBadge>{badge}</StatusBadge> : null}
                </ItemIdentity>
            : null}
            {story ?
                <Typography component="blockquote" sx={{ m: 0, mt: item ? 0 : '15px', overflowWrap: 'anywhere' }}>
                    {story}
                </Typography>
            : null}
        </ItemCard>
    );
}
